<script setup lang="ts">
import {
  bsModalState, createJudgmentOption,
  editMode,
  indicatorsVisible,
  judgmentOptions,
  kdm,
  percentagesVisible,
  discussionState,
  getCommentIcon,
  loadKdm,
  drawerVisible,
  selectedCell,
  cellIndicatorsMapping,
  getIndicatorInfo,
  computeCellWinner,
  addAxis,
  deleteAxis,
  addStakeholder,
  checkAxisUsage,
  generateAiEvaluations,
  aiEvaluating,
  viewAxis,
  activePivotId,
  activePivotTab,
  pivotAxisItems,
  axisRecycle,
  openAxisRecycle,
  reportVisible,
  activeStakeholderId,
  resetMatrixAll,
  resetting,
  type AxisUsageInfo,
} from "@/store/kdm";
import {useFetch} from "@/composables/useFetch";
import AxisRecycleDrawer from "@/components/AxisRecycleDrawer.vue";
import CloneMatrixModal from "@/components/CloneMatrixModal.vue";
import Tab from "primevue/tab";
import TabPanel from "primevue/tabpanel";
import TabList from "primevue/tablist";
import KDMTable from "@/kdm/KDMTable.vue";
import KDMPivotTable from "@/kdm/KDMPivotTable.vue";
import ToggleSwitch from "primevue/toggleswitch";
// Direct import - async loading causes Vue DOM patching issues in production
import KDMCube3D from "@/kdm/KDMCube3D.vue";
import TabPanels from "primevue/tabpanels";
import Tabs from "primevue/tabs";
import {useI18n} from "vue-i18n";
import SpinnerIcon from "@/icons/SpinnerIcon.vue";
import KDMJudgmentOption from "@/kdm/KDMJudgmentOption.vue";
import KDMAssignmentsModal from "@/kdm/KDMAssignmentsModal.vue";
import MatrixAxisCarousel from "@/components/MatrixAxisCarousel.vue";
import MatrixLegend from "@/components/MatrixLegend.vue";
import KnotView from "@/kdm/KnotView.vue";
import DeliberationReport from "@/kdm/DeliberationReport.vue";
import KdmEditableAxisLabel from "@/kdm/KdmEditableAxisLabel.vue";
import {ref, watch, computed, onMounted, onUnmounted, Teleport} from "vue";
import {useDebounceFn} from "@vueuse/core";
import {CROSSLINKS_CHANGED} from "@/composables/crosslinkEvents";
import {useLockStatus} from "@/composables/useLockStatus";

const ofetch = useFetch();

// Track if user has ever activated edit mode (disables animation permanently until reload)
const hasActivatedEditMode = ref(false);
watch(editMode, (newVal) => {
  if (newVal) hasActivatedEditMode.value = true;
});

const {t} = useI18n()

// Lock status polling with data change detection
const nid = computed(() => kdm.value?.nid ? String(kdm.value.nid) : null);
const {
  lockStatus,
  isLockedByOther,
  lockHolder,
  hasRemoteChanges,
  secondsUntilRelease,
  startPolling,
  stopPolling,
  updateFromResponse,
  acquireLock,
  releaseLock,
  resetDataHash,
  setDataHashFromStatus
} = useLockStatus(nid, {
  pollInterval: 10000,
  heartbeatInterval: 15000,
  inactivityTimeout: 120000, // 2 minutes
  onLockLost: () => {
    console.warn('Lock was lost!');
    editMode.value = false;
  },
  onDataChanged: () => {
    console.log('Remote data change detected!');
  },
  onInactivityTimeout: () => {
    // Lock was auto-released due to inactivity - just exit edit mode
    editMode.value = false;
  }
});

// Can current user force-unlock?
const canForceUnlock = computed(() => {
  const cu = kdm.value?.current_user;
  return cu?.is_admin || cu?.is_owner || cu?.is_coordinator || cu?.is_editor;
});

// Force unlock the matrix (for owner/coordinator)
async function forceUnlock() {
  if (!nid.value || !canForceUnlock.value) return;

  if (!confirm(t('epb.lock.force_unlock_confirm', { name: lockHolder.value }))) {
    return;
  }

  try {
    await ofetch(`/epbapi/matrix/${nid.value}/lock-force-release`, { method: 'POST' });
    // Refresh lock status
    await updateFromResponse(await (await fetch(`/epbapi/matrix/${nid.value}/lock-status`)).json());
  } catch (e) {
    console.error('Failed to force unlock:', e);
    alert(t('epb.lock.force_unlock_failed'));
  }
}

// Watch editMode to acquire/release lock
watch(editMode, async (newVal, oldVal) => {
  if (newVal && !oldVal) {
    // Entering edit mode - acquire lock
    const result = await acquireLock();
    if (!result.success) {
      // Failed to acquire lock - revert edit mode
      editMode.value = false;
      alert(t('epb.lock.cannot_acquire', { reason: result.message || lockHolder.value || 'unknown' }));
    }
  } else if (!newVal && oldVal) {
    // Exiting edit mode - release lock
    await releaseLock();
  }
});

// Update lock status when kdm loads
const updateLockFromKdm = () => {
  if (kdm.value?.lock_status) {
    updateFromResponse(kdm.value.lock_status);
    // Initialize data hash from server's hash
    if (kdm.value.lock_status.data_hash) {
      setDataHashFromStatus(kdm.value.lock_status.data_hash);
    }
  }
};

// Check if a stakeholder is assigned to current user
const isStakeholderAssignedToMe = (stakeholderId: string) => {
  return kdm.value?.user_assigned_stakeholders?.includes(stakeholderId) ?? false;
};

// Refresh data (for when remote changes detected)
async function refreshData() {
  if (kdm.value?.nid) {
    await loadKdm(kdm.value.nid);
    await resetDataHash(); // This fetches fresh hash from server
  }
}

// Reload the matrix (and Knot view) when crosslinks change anywhere on the page
// (e.g. reorder/CUD from the Crosslinks block). Debounced to collapse bursts.
const reloadOnCrosslinksChanged = useDebounceFn(() => {
  if (kdm.value?.nid) loadKdm(String(kdm.value.nid));
}, 300);

onMounted(() => {
  updateLockFromKdm();
  startPolling();
  document.addEventListener(CROSSLINKS_CHANGED, reloadOnCrosslinksChanged);
  // Initialize the active stakeholder to the first tab.
  if (kdm.value?.stake_holders?.length) {
    activeStakeholderId.value = kdm.value.stake_holders[0].id;
  }
})

onUnmounted(async () => {
  stopPolling();
  document.removeEventListener(CROSSLINKS_CHANGED, reloadOnCrosslinksChanged);
  // Release lock if we hold it when leaving
  if (lockStatus.value.locked_by_me) {
    await releaseLock();
  }
})

const addingJudgmentOption = ref(false)
const assignmentsModal = ref<InstanceType<typeof KDMAssignmentsModal> | null>(null);

const judgmentOptionEntries = computed(() => Array.from(judgmentOptions.value.entries()));
// Legend items (judgment value swatches) for the View's bottom legend.
const legendItems = computed(() =>
  Array.from(judgmentOptions.value.values()).map((j: any) => ({ title: j.title, color: j.color }))
);
// Wording of the red "no longer in the KIK" flag follows the global mode
// (crosslinks vs context) — "link" is avoided (too close to Crosslink).
const kikNoteText = computed(() => (kdm.value as any)?.use_crosslinks_for_ipr
  ? 'Indicator/Argument no longer crosslinked to the KIK'
  : 'Indicator/Argument no longer contextualized in the KIK');
// Show the legend's "no longer in the KIK" note ONLY when this View actually
// mobilizes a missing indicator/argument.
const hasMissingIpr = computed(() =>
  ((kdm.value as any)?.iprs || []).some((ipr: any) => ipr.is_in_kik === false)
);

// True when at least one evaluation (judgment) value is defined. When none, the
// Evaluation Values button is highlighted (bg-warning) to invite defining them.
const hasEvaluationValues = computed(() => judgmentOptions.value.size > 0);

async function addJudgmentOption(): Promise<void> {
  addingJudgmentOption.value = true
  try {
    await createJudgmentOption()
  } finally {
    addingJudgmentOption.value = false
  }
}

const userCanEditEvaluationValues = window.drupalSettings.userCanEdit
const canManageAssignments = computed(() => {
  const cu = kdm.value?.current_user;
  return cu?.is_admin || cu?.is_owner || cu?.is_coordinator || cu?.is_editor;
});

// The AI Evaluation feature (billed Claude API) is restricted to the matrix
// owner or a Group coordinator. Enforced server-side in KdmController::aiEvaluate.
const canAiEvaluate = computed(() => {
  const cu = kdm.value?.current_user;
  return !!(cu?.is_owner || cu?.is_coordinator);
});

// The multi-page Deliberation Matrix Report is for the matrix owner or a Group
// coordinator only.
const canOpenReport = computed(() => {
  const cu = kdm.value?.current_user;
  return !!(cu?.is_owner || cu?.is_coordinator);
});

// Clone this KDM (owner / coordinator).
const cloneVisible = ref(false);

// Trigger AI auto-fill of empty cells for the "IA Evaluation" stakeholder.
async function handleAiEvaluate() {
  if (aiEvaluating.value) return;
  if (!confirm(t('epb.kdm.ai_evaluate_confirm'))) return;
  const result = await generateAiEvaluations();
  alert(result.message);
  // Land on the (new or updated) IA Evaluation stakeholder matrix. The matrix
  // and crosslinks block are already refreshed by generateAiEvaluations().
  if (result.stakeholderId) {
    activeStakeholderId.value = result.stakeholderId;
  }
}

// User can edit if they have edit permissions OR assigned to at least one stakeholder
const canEnterEditMode = computed(() => {
  const cu = kdm.value?.current_user;
  if (cu?.is_admin || cu?.is_owner || cu?.is_coordinator || cu?.is_editor) return true;
  const assignedStakeholders = kdm.value?.user_assigned_stakeholders || [];
  return assignedStakeholders.length > 0;
});

const canEditEvaluationValues = computed(() => {
  return canManageAssignments.value;
});

const globalCommentIcon = computed(() => getCommentIcon(kdm.value?.nid || ''))

// 3D View toggle
const show3DView = ref(false);
// Knot View toggle (tabular view of all evaluations)
const showKnotView = ref(false);
const show3DAxes = ref(true); // ON by default
const show3DFilters = ref(false);
const show3DLabels = ref(true); // ON by default
const show3DAxisManager = ref(false);
const newRowTitle = ref('');
const newColumnTitle = ref('');
const newStakeholderTitle = ref('');
const addingAxis = ref(false);

// 2D column width slider (150px default, 30px min, 300px max)
const columnWidth = ref(150);
const columnWidthPreview = ref(150); // Preview value while dragging

// Axis deletion modal state
const axisDeleteModal = ref({
  visible: false,
  loading: false,
  axisType: '' as 'row' | 'column' | 'stakeholder',
  crossLinkId: '',
  axisNodeId: '',
  title: '',
  deleteNode: false,
  usage: null as AxisUsageInfo | null,
});

async function openAxisDeleteModal(axisType: 'row' | 'column' | 'stakeholder', crossLinkId: string, axisNodeId: string, title: string) {
  axisDeleteModal.value = {
    visible: true,
    loading: true,
    axisType,
    crossLinkId,
    axisNodeId,
    title,
    deleteNode: false,
    usage: null,
  };

  // Fetch usage info
  const usage = await checkAxisUsage(axisNodeId);
  axisDeleteModal.value.loading = false;
  axisDeleteModal.value.usage = usage;
}

function closeAxisDeleteModal() {
  axisDeleteModal.value.visible = false;
}

async function confirmAxisDelete() {
  const { axisType, crossLinkId, deleteNode } = axisDeleteModal.value;
  axisDeleteModal.value.loading = true;

  const success = await deleteAxis(axisType, crossLinkId, deleteNode);

  axisDeleteModal.value.loading = false;

  if (success) {
    closeAxisDeleteModal();
    // Reload KDM to get fresh data (especially for cascade deletions)
    if (kdm.value?.nid) {
      await loadKdm(String(kdm.value.nid));
    }
  }
}

// Can current user manage axes?
const canManageAxis = computed(() => {
  const cu = kdm.value?.current_user;
  return cu?.is_admin || cu?.is_owner || cu?.is_coordinator || cu?.is_editor;
});

// Axis management functions for 3D
async function handleAddRow() {
  if (!newRowTitle.value.trim() || addingAxis.value) return;
  addingAxis.value = true;
  const success = await addAxis('row', newRowTitle.value.trim());
  if (success) newRowTitle.value = '';
  addingAxis.value = false;
}

async function handleAddColumn() {
  if (!newColumnTitle.value.trim() || addingAxis.value) return;
  addingAxis.value = true;
  const success = await addAxis('column', newColumnTitle.value.trim());
  if (success) newColumnTitle.value = '';
  addingAxis.value = false;
}

async function handleAddStakeholder() {
  if (!newStakeholderTitle.value.trim() || addingAxis.value) return;
  addingAxis.value = true;
  const success = await addStakeholder(newStakeholderTitle.value.trim());
  if (success) newStakeholderTitle.value = '';
  addingAxis.value = false;
}

// Reload the matrix after an existing briquette was recycled as an axis value.
async function onAxisRecycled() {
  if (kdm.value?.nid) await loadKdm(String(kdm.value.nid));
}

async function handleDeleteAxis(axisType: 'row' | 'column' | 'stakeholder', crossLinkId: string, axisNodeId: string, title: string) {
  await openAxisDeleteModal(axisType, crossLinkId, axisNodeId, title);
}
const show3DAllOpaque = ref(true); // ON by default
const show3DCellDetailMode = ref(true); // ON by default

// 3D Cell detail drawer state
const selected3DCellId = ref<string | null>(null);
const selected3DCellInfo = ref<{
  stakeholderTitle: string;
  rowTitle: string;
  colTitle: string;
  stakeholderIndex: number;
  rowIndex: number;
  colIndex: number;
} | null>(null);

// 3D slice axis and indices
const sliceAxis = ref<'x' | 'y' | 'z'>('z');
const sliceIndices = ref<{ x: number; y: number; z: number }>({ x: 0, y: 0, z: 0 });

// 3D filtering - starts with all values visible
const visible3DEvaluationValues = ref<Set<string>>(new Set());

// Keep filter set in sync with judgment values - add new ones automatically
watch(judgmentOptions, (newOptions, oldOptions) => {
  if (newOptions.size === 0) return;

  // Initialize if empty
  if (visible3DEvaluationValues.value.size === 0) {
    visible3DEvaluationValues.value = new Set(newOptions.keys());
    return;
  }

  // Add any new judgment values that weren't in the old set
  const oldKeys = oldOptions ? new Set(oldOptions.keys()) : new Set();
  let hasNewValues = false;
  const updatedSet = new Set(visible3DEvaluationValues.value);

  for (const key of newOptions.keys()) {
    if (!oldKeys.has(key)) {
      // This is a new value - add it to visible set
      updatedSet.add(key);
      hasNewValues = true;
    }
  }

  if (hasNewValues) {
    visible3DEvaluationValues.value = updatedSet;
  }
}, { immediate: true });

// Toggle evaluation value visibility in 3D
function toggle3DEvaluationValue(id: string) {
  const newSet = new Set(visible3DEvaluationValues.value);
  if (newSet.has(id)) {
    newSet.delete(id);
  } else {
    newSet.add(id);
  }
  visible3DEvaluationValues.value = newSet;
}

// Show all evaluation values (reset filters)
function showAll3DEvaluationValues() {
  visible3DEvaluationValues.value = new Set(judgmentOptions.value.keys());
}

// Check if any filter is hiding values (not all are visible)
const has3DFilters = computed(() => {
  return visible3DEvaluationValues.value.size < judgmentOptions.value.size;
});

// Count cells per evaluation value for 3D badges
const cellCountsByEvaluationValue = computed(() => {
  const counts = new Map<string, number>();
  if (!kdm.value) return counts;

  const stakeholders = kdm.value.stake_holders || [];
  const rows = kdm.value.rows || [];
  const cols = kdm.value.columns || [];

  let matrixData: Record<string, any> = {};
  if (kdm.value.field_matrix_data) {
    try {
      matrixData = typeof kdm.value.field_matrix_data === 'string'
        ? JSON.parse(kdm.value.field_matrix_data)
        : kdm.value.field_matrix_data;
    } catch (e) {
      return counts;
    }
  }

  const judgmentOrder = kdm.value?.judgment_values?.map(jv => String(jv.id)) || [];

  stakeholders.forEach((sh) => {
    rows.forEach((row) => {
      cols.forEach((col) => {
        const cellId = `${sh.id}-${row.id}-${col.id}`;
        const cellData = matrixData[cellId];
        if (!cellData) return;

        let winnerId: string | null = null;

        if (kdm.value?.kdm_type === 1) {
          winnerId = cellData.judgmentId || null;
        } else {
          // Aggregation logic for type 2
          const indicators = Object.values(cellData).filter((v: any) =>
            v && typeof v === 'object' && v.judgmentId
          );
          if (indicators.length > 0) {
            const weightsByJudgment = new Map<string, number>();
            indicators.forEach((ind: any) => {
              const current = weightsByJudgment.get(ind.judgmentId) || 0;
              weightsByJudgment.set(ind.judgmentId, current + (ind.sliderValue || 0));
            });

            let maxWeight = -1;
            for (const evalId of judgmentOrder) {
              const weight = weightsByJudgment.get(evalId) || 0;
              if (weight > maxWeight) {
                maxWeight = weight;
                winnerId = evalId;
              }
            }
          }
        }

        if (winnerId) {
          counts.set(winnerId, (counts.get(winnerId) || 0) + 1);
        }
      });
    });
  });

  return counts;
});

// Current active slice index based on axis
const activeSliceIndex = computed(() => sliceIndices.value[sliceAxis.value]);

// Get cell data for 3D drawer - combines saved data with pending indicators
const selected3DCellData = computed(() => {
  // Dependency on trigger for forced re-evaluation
  const _trigger = cellDataTrigger.value;

  if (!selected3DCellId.value || !kdm.value) return null;

  let matrixData: Record<string, any> = {};
  if (kdm.value.field_matrix_data) {
    try {
      matrixData = typeof kdm.value.field_matrix_data === 'string'
        ? JSON.parse(kdm.value.field_matrix_data)
        : kdm.value.field_matrix_data;
    } catch (e) {
      return null;
    }
  }

  const cellData = matrixData[selected3DCellId.value] || {};

  if (kdm.value.kdm_type === 1) {
    // KDM Type 1: Simple cell with single judgment
    const judgment = judgmentOptions.value.get(cellData.judgmentId);
    return {
      indicators: [],
      pendingIndicators: [],
      winningJudgment: judgment ? {
        id: cellData.judgmentId,
        label: judgment.title || judgment.color,
        color: judgment.color,
        sliderValue: cellData.sliderValue || 0
      } : null
    };
  }

  // KDM Type 2/3: Multiple indicators
  const indicators: Array<{
    id: string;
    title: string;
    href: string;
    judgmentId: string;
    judgmentLabel: string;
    judgmentColor: string;
    sliderValue: number;
  }> = [];

  // Pending indicators (from cellIndicatorsMapping but not yet saved with judgmentId)
  const pendingIndicators: Array<{
    id: string;
    title: string;
    href: string;
  }> = [];

  const processedIds = new Set<string>();

  // Process saved indicators from field_matrix_data
  Object.entries(cellData).forEach(([indicatorId, data]: [string, any]) => {
    if (!data || typeof data !== 'object') return;
    processedIds.add(indicatorId);

    // Skip indicators without judgmentId - they go to pending
    if (!data.judgmentId) {
      const info = getIndicatorInfo(indicatorId);
      pendingIndicators.push({
        id: indicatorId,
        title: info.title,
        href: info.href || `/node/${indicatorId}`
      });
      return;
    }

    const info = getIndicatorInfo(indicatorId);
    const judgment = judgmentOptions.value.get(data.judgmentId);

    indicators.push({
      id: indicatorId,
      title: info.title || indicatorId,
      href: info.href || `/node/${indicatorId}`,
      judgmentId: data.judgmentId,
      judgmentLabel: judgment?.title || judgment?.color || '',
      judgmentColor: judgment?.color || '#ccc',
      sliderValue: data.sliderValue || 0
    });
  });

  // Add indicators from cellIndicatorsMapping that aren't in field_matrix_data yet
  const cellMapping = cellIndicatorsMapping[selected3DCellId.value];
  if (cellMapping) {
    cellMapping.forEach((info, indicatorId) => {
      if (!processedIds.has(indicatorId)) {
        pendingIndicators.push({
          id: indicatorId,
          title: info.title || indicatorId,
          href: `/node/${indicatorId}`
        });
      }
    });
  }

  // Use shared algorithm for winning judgment calculation
  const cellWinner = computeCellWinner(cellData, kdm.value?.kdm_type);

  return {
    indicators: indicators.sort((a, b) => a.title.localeCompare(b.title)),
    pendingIndicators: pendingIndicators.sort((a, b) => a.title.localeCompare(b.title)),
    winningJudgment: cellWinner ? {
      id: cellWinner.judgmentId,
      label: judgmentOptions.value.get(cellWinner.judgmentId)?.title || cellWinner.color,
      color: cellWinner.color,
      totalWeight: cellWinner.totalWeight
    } : null
  };
});

// Local state for editing pending indicators in 3D drawer
const pending3DIndicatorValues = ref<Map<string, { judgmentId: string; sliderValue: number }>>(new Map());

// Trigger for forcing computed re-evaluation
const cellDataTrigger = ref(0);

// Reset pending values when cell changes
watch(selected3DCellId, () => {
  pending3DIndicatorValues.value = new Map();
});

// Update a pending indicator value (for reactivity)
function updatePending3DIndicatorValue(indicatorId: string, updates: Partial<{ judgmentId: string; sliderValue: number }>) {
  const current = pending3DIndicatorValues.value.get(indicatorId) || { judgmentId: '', sliderValue: 50 };
  const updated = { ...current, ...updates };
  const newMap = new Map(pending3DIndicatorValues.value);
  newMap.set(indicatorId, updated);
  pending3DIndicatorValues.value = newMap;
}

// Get pending value for an indicator
function getPending3DValue(indicatorId: string) {
  return pending3DIndicatorValues.value.get(indicatorId) || { judgmentId: '', sliderValue: 50 };
}

// Save a pending indicator with evaluation value and weight
async function save3DPendingIndicator(indicatorId: string) {
  if (!selected3DCellId.value || !kdm.value) return;

  const values = pending3DIndicatorValues.value.get(indicatorId);
  if (!values || !values.judgmentId) {
    alert('Please select an Evaluation Value');
    return;
  }

  // Prepare the update
  let matrixData: Record<string, any> = {};
  if (kdm.value.field_matrix_data) {
    try {
      matrixData = typeof kdm.value.field_matrix_data === 'string'
        ? JSON.parse(kdm.value.field_matrix_data)
        : { ...kdm.value.field_matrix_data };
    } catch (e) {
      matrixData = {};
    }
  }

  const cellData = { ...(matrixData[selected3DCellId.value] || {}) };
  cellData[indicatorId] = {
    judgmentId: values.judgmentId,
    sliderValue: values.sliderValue
  };

  const requestBody: Record<string, any> = {};
  requestBody[selected3DCellId.value] = cellData;

  const nid = kdm.value.nid;

  try {
    await ofetch<any>(`/epbapi/kdm/${nid}/`, {
      method: 'PATCH',
      body: requestBody,
    });

    // Remove from cellIndicatorsMapping since it's now saved
    const mapping = cellIndicatorsMapping[selected3DCellId.value];
    if (mapping) {
      mapping.delete(indicatorId);
    }

    // Clear the pending value
    const newMap = new Map(pending3DIndicatorValues.value);
    newMap.delete(indicatorId);
    pending3DIndicatorValues.value = newMap;

    // Reload to ensure 3D cube updates correctly
    await loadKdm(String(nid));
  } catch (err: any) {
    console.error("Failed to save indicator:", err);
    alert(`Error saving: ${err.data?.message || 'Unknown error'}`);
    await loadKdm(String(nid));
  }
}

// Remove a pending indicator (cancel addition)
function remove3DPendingIndicator(indicatorId: string) {
  if (!selected3DCellId.value) return;

  // Remove from cellIndicatorsMapping
  const mapping = cellIndicatorsMapping[selected3DCellId.value];
  if (mapping) {
    mapping.delete(indicatorId);
  }

  // Clear any pending values
  const newMap = new Map(pending3DIndicatorValues.value);
  newMap.delete(indicatorId);
  pending3DIndicatorValues.value = newMap;

  // Force re-evaluation of computed
  cellDataTrigger.value++;
}

// Remove a saved indicator from a cell (delete from matrix data)
async function remove3DSavedIndicator(indicatorId: string) {
  if (!selected3DCellId.value || !kdm.value) return;

  // Get current matrix data
  let matrixData: Record<string, any> = {};
  if (kdm.value.field_matrix_data) {
    try {
      matrixData = typeof kdm.value.field_matrix_data === 'string'
        ? JSON.parse(kdm.value.field_matrix_data)
        : { ...kdm.value.field_matrix_data };
    } catch (e) {
      matrixData = {};
    }
  }

  const cellData = { ...(matrixData[selected3DCellId.value] || {}) };
  delete cellData[indicatorId];

  const requestBody: Record<string, any> = {};
  requestBody[selected3DCellId.value] = cellData;

  const nid = kdm.value.nid;

  try {
    await ofetch<any>(`/epbapi/kdm/${nid}/`, {
      method: 'PATCH',
      body: requestBody,
    });

    // Reload to ensure 3D cube updates correctly
    await loadKdm(String(nid));
  } catch (err: any) {
    console.error("Failed to remove indicator:", err);
    alert(`Error removing indicator: ${err.data?.message || 'Unknown error'}`);
    await loadKdm(String(nid));
  }
}

// Get comment icon for selected 3D cell (same logic as 2D view)
const selected3DCellCommentIcon = computed(() => {
  if (!selected3DCellId.value) return { icon: 'bi-chat', class: 'text-muted opacity-50' };
  return getCommentIcon(selected3DCellId.value);
});

// Check if selected 3D cell is locked for current user (same logic as KDMCell.vue)
const isSelected3DCellLocked = computed(() => {
  if (!selected3DCellId.value || !kdm.value?.current_user) return true;

  const cu = kdm.value.current_user;
  // Admins, owners, coordinators, and editors can edit any cell
  if (cu.is_admin || cu.is_owner || cu.is_coordinator || cu.is_editor) return false;

  // Get the stakeholder ID from selected cell info
  const stakeholderIndex = selected3DCellInfo.value?.stakeholderIndex;
  if (stakeholderIndex === undefined || stakeholderIndex === null) return true;

  const stakeholder = kdm.value.stake_holders?.[stakeholderIndex];
  if (!stakeholder) return true;

  // Parse matrix data to get assignments
  let matrixData: any = {};
  if (kdm.value.field_matrix_data) {
    if (typeof kdm.value.field_matrix_data === 'string') {
      try { matrixData = JSON.parse(kdm.value.field_matrix_data) || {}; } catch (e) { matrixData = {}; }
    } else {
      matrixData = kdm.value.field_matrix_data || {};
    }
  }

  const assignments = matrixData.assignments || {};
  let assignedIds = assignments[stakeholder.id];
  if (!assignedIds) {
    const key = Object.keys(assignments).find(k => String(k) === String(stakeholder.id));
    if (key) assignedIds = assignments[key];
  }

  const allowedUserIds = (assignedIds || []).map((id: any) => String(id));
  const currentUserId = String(kdm.value.current_user.id);

  return !allowedUserIds.includes(currentUserId);
});

// Get comment icon for a specific indicator in the selected cell
function getIndicatorCommentIcon(indicatorId: string) {
  if (!selected3DCellId.value) return { icon: 'bi-chat', class: 'text-muted opacity-50' };
  return getCommentIcon(`${selected3DCellId.value}-${indicatorId}`);
}

// Handle cell click from 3D view
function on3DCellClick(cellId: string, stakeholderIndex: number, rowIndex: number, colIndex: number) {
  selected3DCellId.value = cellId;
  selected3DCellInfo.value = {
    stakeholderTitle: kdm.value?.stake_holders?.[stakeholderIndex]?.title || '',
    rowTitle: kdm.value?.rows?.[rowIndex]?.title || '',
    colTitle: kdm.value?.columns?.[colIndex]?.title || '',
    stakeholderIndex,
    rowIndex,
    colIndex
  };
}

// Close 3D cell drawer
function close3DCellDrawer() {
  selected3DCellId.value = null;
  selected3DCellInfo.value = null;
}

// Open discussion for selected 3D cell
function open3DCellDiscussion() {
  if (!selected3DCellId.value) return;
  discussionState.coords = selected3DCellId.value;
  discussionState.nodeId = kdm.value?.nid || '';
  discussionState.canWrite = canManageAssignments.value;
  discussionState.canClear = canOpenReport.value;
  discussionState.visible = true;
}

// Open discussion for a specific indicator in the selected 3D cell
function open3DIndicatorDiscussion(indicatorId: string) {
  if (!selected3DCellId.value) return;
  discussionState.coords = `${selected3DCellId.value}-${indicatorId}`;
  discussionState.nodeId = kdm.value?.nid || '';
  discussionState.canWrite = canManageAssignments.value;
  discussionState.canClear = canOpenReport.value;
  discussionState.visible = true;
}

// Open the indicator drawer to add indicators to the selected 3D cell
function open3DIndicatorDrawer() {
  if (!selected3DCellId.value) return;
  if (isSelected3DCellLocked.value) return; // Same check as 2D view
  selectedCell.value = selected3DCellId.value;
  drawerVisible.value = true;
}

// Get slice items for current axis
const currentSliceItems = computed(() => {
  if (!kdm.value) return [];
  if (sliceAxis.value === 'z') return kdm.value.stake_holders || [];
  if (sliceAxis.value === 'x') return kdm.value.columns || [];
  return kdm.value.rows || [];
});

// Get current slice title
const currentSliceTitle = computed(() => {
  const items = currentSliceItems.value;
  const idx = activeSliceIndex.value;
  return items[idx]?.title || '';
});

// Handle slice selection from 3D cube (when clicking axis labels)
function onSelectSlice(axis: 'x' | 'y' | 'z', index: number) {
  // If clicking on a different axis, switch to that perspective
  if (axis !== sliceAxis.value) {
    sliceAxis.value = axis;
  }
  // Update the index for that axis
  sliceIndices.value[axis] = index;

  // Also sync with 2D view if Z axis (stakeholders)
  if (axis === 'z') {
    const holders = kdm.value?.stake_holders || [];
    if (holders[index]) {
      activeStakeholderId.value = holders[index].id;
    }
  }
}

// Sync Z slice index when stakeholder tab changes in 2D view
watch(activeStakeholderId, (newId) => {
  const holders = kdm.value?.stake_holders || [];
  const idx = holders.findIndex(sh => sh.id === newId);
  if (idx >= 0) {
    sliceIndices.value.z = idx;
  }
});

// Close cell drawer when slice changes (selected cell may no longer be in active slice)
watch([sliceAxis, activeSliceIndex], () => {
  if (selected3DCellId.value) {
    close3DCellDrawer();
  }
});

// Carousel items for 2D view navigation
const carouselItems = computed(() => {
  return (kdm.value?.stake_holders || []).map(sh => ({
    id: sh.id,
    title: sh.title,
    isAssigned: isStakeholderAssignedToMe(sh.id)
  }));
});

// The currently displayed stakeholder — target of inline rename / delete in 2D.
const activeStakeholder = computed(() => {
  const holders = kdm.value?.stake_holders || [];
  return holders.find(sh => sh.id === activeStakeholderId.value) || holders[0] || null;
});

// Keep the active stakeholder valid after one is added or removed.
watch(() => (kdm.value?.stake_holders || []).map(sh => sh.id).join(','), () => {
  const holders = kdm.value?.stake_holders || [];
  if (holders.length && !holders.some(sh => sh.id === activeStakeholderId.value)) {
    activeStakeholderId.value = holders[0].id;
  }
});

// ---- Axis pivot (state lives in the store; the toggle is rendered in the table
// corner cell via KDMViewAxisToggle). Carousel tabs for the pivot axis. ----
const pivotCarouselItems = computed(() =>
  pivotAxisItems.value.map(a => ({ id: a.id, title: a.title, isAssigned: false }))
);

function openGlobalDiscussion() {
  discussionState.coords = kdm.value?.nid || '';
  discussionState.nodeId = kdm.value?.nid || '';
  // Global discussion: write access for anyone who can edit the matrix
  discussionState.canWrite = canManageAssignments.value;
  discussionState.canClear = canOpenReport.value;
  discussionState.visible = true;
}

watch(editMode, (newVal) => {
  if (newVal) {
    indicatorsVisible.value = true
  }
})

// Flag the <body> while the report overlay is open so the global print rule can
// print only the report (the overlay is teleported to body). Also released on
// unmount so leaving the page never leaves a stale class behind.
watch(reportVisible, (open) => {
  document.body.classList.toggle('kdm-report-open', open);
});
onUnmounted(() => {
  document.body.classList.remove('kdm-report-open');
});
</script>

<template>
  <!-- Remote changes notification banner -->
  <div v-if="hasRemoteChanges" class="alert alert-warning d-flex align-items-center justify-content-between mb-3" role="alert">
    <div>
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ t('epb.sync.remote_changes') }}
    </div>
    <button type="button" class="btn btn-warning btn-sm" @click="refreshData">
      <i class="bi bi-arrow-clockwise me-1"></i>
      {{ t('epb.sync.refresh') }}
    </button>
  </div>

  <div class="py-2">
    <!-- Single compact toolbar row (icons + tooltips), wraps on narrow widths. -->
    <div class="d-flex flex-wrap align-items-center gap-2">
      <!-- Lock Status Indicator -->
      <div v-if="lockStatus.is_locked && lockStatus.locked_by_me" class="lock-status-indicator d-flex align-items-center gap-1">
        <i class="bi bi-lock-fill text-success"></i>
        <span class="small text-success">
          {{ t('epb.lock.you_have_lock_for', { seconds: secondsUntilRelease }) }}
        </span>
      </div>
      <div v-else-if="lockStatus.is_locked" class="lock-status-indicator d-flex align-items-center gap-1">
        <i class="bi bi-lock-fill text-danger"></i>
        <span class="small text-danger">{{ t('epb.lock.locked_by', { name: lockHolder }) }}</span>
        <!-- Force unlock button for owner/coordinator -->
        <button
          v-if="canForceUnlock"
          type="button"
          class="btn btn-link btn-sm text-danger p-0 ms-2"
          @click="forceUnlock"
          :title="t('epb.lock.force_unlock')"
        >
          <i class="bi bi-unlock-fill"></i>
        </button>
      </div>
      <div v-else class="lock-status-indicator d-flex align-items-center gap-1">
        <i class="bi bi-unlock text-muted"></i>
        <span class="small text-muted">{{ t('epb.lock.unlocked') }}</span>
      </div>

      <!-- Edit mode toggle — animated pencil, icon only, placed just left of
           User Assignment. -->
      <div class="form-check form-switch mb-0 d-flex align-items-center" v-if="canEnterEditMode" :title="t('epb.kdm.toggle_edit_mode')">
        <input class="form-check-input" type="checkbox" v-model="editMode" id="editModeToggleKdm" :disabled="isLockedByOther">
        <label class="form-check-label ms-2" for="editModeToggleKdm">
          <i class="bi bi-pencil-fill" :class="{ 'animate-bounce': !hasActivatedEditMode }"></i>
        </label>
      </div>

      <div v-if="canManageAssignments">
        <button
          type="button"
          class="btn btn-outline-primary btn-sm"
          @click="assignmentsModal?.open()"
          :title="t('epb.kdm.user_assignments')"
        >
          <i class="bi bi-people-fill"></i>
        </button>
      </div>

      <div>
        <button v-if="!bsModalState && canEditEvaluationValues"
                type="button"
                class="btn btn-sm"
                :class="hasEvaluationValues ? 'btn-outline-dark' : 'btn-warning'"
                data-bs-toggle="modal"
                data-bs-target="#evaluationValuesModal"
                :title="hasEvaluationValues ? t('epb.kdm.judgment_values') : t('epb.kdm.judgment_values') + ' — none defined yet'">
          <i class="bi bi-palette-fill"></i>
        </button>
      </div>

      <!-- AI Evaluation: auto-fill empty cells for the "IA Evaluation" stakeholder.
           Owner or Group coordinator only (also enforced server-side). -->
      <div v-if="canAiEvaluate">
        <button
          type="button"
          class="btn btn-outline-primary btn-sm"
          @click="handleAiEvaluate"
          :disabled="aiEvaluating"
          :title="t('epb.kdm.ai_evaluate_title')"
        >
          <span v-if="aiEvaluating" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          <i v-else class="bi bi-stars"></i>
        </button>
      </div>
      <!-- Clone this KDM — owner/coordinator. -->
      <div v-if="canAiEvaluate">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          @click="cloneVisible = true"
          title="Clone this KDM (structure + data, never comments)"
        >
          <i class="bi bi-files"></i>
        </button>
      </div>
      <!-- Reset the WHOLE matrix (cell content + comments) — owner/coordinator.
           Kept last/rightmost as the most destructive action. -->
      <div v-if="canAiEvaluate">
        <button
          type="button"
          class="btn btn-outline-danger btn-sm"
          :disabled="resetting"
          @click="resetMatrixAll()"
          title="Reset the whole matrix — delete all cell content and comments (axes and indicators are kept)"
        >
          <span v-if="resetting" class="spinner-border spinner-border-sm me-1" role="status"></span>
          <i v-else class="bi bi-arrow-counterclockwise"></i>
        </button>
      </div>
      <!-- Show indicators — icon button (replaces the old text toggle). -->
      <div v-if="kdm?.kdm_type !== 1">
        <button
          type="button"
          class="btn btn-sm"
          :class="indicatorsVisible ? 'btn-info' : 'btn-outline-info'"
          @click="indicatorsVisible = !indicatorsVisible"
          :title="t('epb.kdm.toggle_indicators')"
        >
          <i class="bi bi-card-list"></i>
        </button>
      </div>

      <!-- Show percentages — icon button (replaces the old text toggle). -->
      <div v-if="kdm?.kdm_type !== 1">
        <button
          type="button"
          class="btn btn-sm"
          :class="percentagesVisible ? 'btn-info' : 'btn-outline-info'"
          @click="percentagesVisible = !percentagesVisible"
          :title="t('epb.kdm.toggle_percentages')"
        >
          <i class="bi bi-percent"></i>
        </button>
      </div>

      <div class="d-flex align-items-center gap-2 ms-auto">
        <!-- Deliberation Matrix Report (owner / Group coordinator only) — opens
             the multi-page report overlay; sits left of the Knot View toggle. -->
        <button
          v-if="canOpenReport"
          type="button"
          class="btn btn-outline-secondary btn-sm"
          @click="reportVisible = true"
          title="Deliberation Matrix Report"
        >
          <i class="bi bi-file-earmark-text"></i>
        </button>
        <!-- Knot View Toggle (tabular view) — available for all KDM types -->
        <button
          type="button"
          class="btn btn-sm"
          :class="showKnotView ? 'btn-info' : 'btn-outline-info'"
          @click="showKnotView = !showKnotView; if (showKnotView) show3DView = false"
          title="Knot View (Tabular)"
        >
          <i class="bi bi-table"></i>
        </button>
        <!-- 3D View Toggle -->
        <button
          type="button"
          class="btn btn-sm"
          :class="show3DView ? 'btn-primary' : 'btn-outline-primary'"
          @click="show3DView = !show3DView; if (show3DView) showKnotView = false"
          :title="show3DView ? 'Vue 2D' : 'Vue 3D'"
        >
          <i class="bi bi-box"></i>
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          @click="openGlobalDiscussion"
          title="Global Discussion"
        >
          <i class="bi" :class="[globalCommentIcon.icon, globalCommentIcon.class]"></i>
        </button>
        <!-- Column width slider (2D only) — narrow, beside full screen. -->
        <div v-if="!show3DView" class="d-flex align-items-center gap-1 column-width-slider" :title="`Column width: ${columnWidthPreview}px`">
          <i class="bi bi-arrows-collapse-vertical text-muted"></i>
          <input
            type="range"
            class="form-range"
            min="30"
            max="300"
            step="10"
            :value="columnWidthPreview"
            @input="(e) => columnWidthPreview = parseInt((e.target as HTMLInputElement).value)"
            @change="(e) => columnWidth = parseInt((e.target as HTMLInputElement).value)"
            style="width: 70px;"
          >
        </div>
        <button v-if="!bsModalState"
                type="button"
                class="btn btn-outline-dark btn-sm"
                data-bs-toggle="modal"
                data-bs-target="#kdmFullScreenModal"
                title="Full screen">
          <i class="bi bi-arrows-fullscreen"></i>
        </button>
      </div>
    </div>
  </div>

  <!-- Knot View (Tabular) — available for all KDM types -->
  <KnotView
    v-if="showKnotView"
    :visible="showKnotView"
    :group-logo="kdm?.group_branding?.logo_url || undefined"
    :group-title="kdm?.group_branding?.title || undefined"
    :can-write="canManageAssignments"
    @close="showKnotView = false"
  />

  <CloneMatrixModal
    v-if="kdm?.nid"
    :visible="cloneVisible"
    :node-id="kdm.nid"
    bundle="epbeo_kdm"
    :kdm-type="kdm?.kdm_type"
    :title="kdm?.title"
    @close="cloneVisible = false"
  />

  <!-- Deliberation Matrix Report — shared 90% drawer chrome, opened from Knot View -->
  <Teleport to="body">
    <div v-if="reportVisible" class="kdm-report-backdrop" @click="reportVisible = false"></div>
    <div v-if="reportVisible" class="kdm-report-overlay epb-pt-drawer is-open">
      <DeliberationReport
        :visible="reportVisible"
        :group-logo="kdm?.group_branding?.logo_url || undefined"
        :group-title="kdm?.group_branding?.title || undefined"
        :can-write="canManageAssignments"
        @close="reportVisible = false"
      />
    </div>
  </Teleport>

  <!-- 3D Cube View -->
  <div v-if="show3DView && !showKnotView && kdm?.kdm_type && kdm?.stake_holders && kdm.stake_holders.length > 0" class="cube-3d-container" :class="{ 'cube-3d-container--fullscreen': bsModalState }">
    <div class="cube-3d-header">
      <!-- Left section: icon + controls -->
      <div class="cube-3d-header__left">
        <i class="bi bi-box"></i>
        <!-- Axis selector buttons (colors match 3D axes: X=red, Y=green, Z=blue) -->
        <div class="cube-3d-axis-selector">
          <button
            type="button"
            class="btn btn-sm"
            :class="sliceAxis === 'x' ? 'btn-danger' : 'btn-outline-danger'"
            @click="sliceAxis = 'x'"
            title="X: Columns"
          >
            X
          </button>
          <button
            type="button"
            class="btn btn-sm"
            :class="sliceAxis === 'y' ? 'btn-success' : 'btn-outline-success'"
            @click="sliceAxis = 'y'"
            title="Y: Rows"
          >
            Y
          </button>
          <button
            type="button"
            class="btn btn-sm"
            :class="sliceAxis === 'z' ? 'btn-primary' : 'btn-outline-primary'"
            @click="sliceAxis = 'z'"
            title="Z: Stakeholders"
          >
            Z
          </button>
        </div>
       <span class="cube-3d-separator"></span>
        <button
          type="button"
          class="btn btn-sm"
          :class="show3DLabels ? 'btn-light' : 'btn-outline-light'"
          @click="show3DLabels = !show3DLabels"
          title="Show labels"
        >
          <i class="bi bi-tag"></i>
        </button>
        <button
          type="button"
          class="btn btn-sm"
          :class="show3DAllOpaque ? 'btn-light' : 'btn-outline-light'"
          @click="show3DAllOpaque = !show3DAllOpaque"
          title="Show all layers at full opacity"
        >
          <i class="bi" :class="show3DAllOpaque ? 'bi-eye-fill' : 'bi-eye'"></i>
        </button>
        <button
          type="button"
          class="btn btn-sm"
          :class="show3DCellDetailMode ? 'btn-light' : 'btn-outline-light'"
          @click="show3DCellDetailMode = !show3DCellDetailMode; if (!show3DCellDetailMode) close3DCellDrawer()"
          title="Cell detail mode"
        >
          <i class="bi bi-info-circle"></i>
        </button>
        <span class="cube-3d-separator"></span>
        <button
          type="button"
          class="btn btn-sm"
          :class="show3DAxes ? 'btn-light' : 'btn-outline-light'"
          @click="show3DAxes = !show3DAxes"
          :title="t('epb.kdm.show_axes')"
        >
          <i class="bi bi-arrows-move"></i>
        </button>
        <button
          type="button"
          class="btn btn-sm position-relative"
          :class="show3DFilters || has3DFilters ? 'btn-light' : 'btn-outline-light'"
          @click="show3DFilters = !show3DFilters"
          :title="t('epb.kdm.filter_values')"
        >
          <i class="bi bi-funnel"></i>
          <span v-if="has3DFilters" class="filter-badge-3d"></span>
        </button>
        <button
          v-if="editMode && canManageAxis"
          type="button"
          class="btn btn-sm"
          :class="show3DAxisManager ? 'btn-light' : 'btn-outline-light'"
          @click="show3DAxisManager = !show3DAxisManager"
          title="Manage axes (X, Y, Z)"
        >
          <i class="bi bi-diagram-3"></i>
        </button>
       </div>
      <!-- Right section: info (fixed width to prevent jiggling) -->
      <span class="cube-3d-info">
        <span class="cube-3d-axis-label">{{ sliceAxis.toUpperCase() }}:</span>
        {{ currentSliceTitle }}
        ({{ activeSliceIndex + 1 }}/{{ currentSliceItems.length }})
      </span>
    </div>

    <!-- Evaluation Values Filter Bar -->
    <div v-if="show3DFilters" class="cube-3d-filters">
      <div class="cube-3d-filters__header">
        <span class="cube-3d-filters__title">{{ t('epb.kdm.judgment_values') }}</span>
        <button
          v-if="has3DFilters"
          type="button"
          class="btn btn-sm btn-outline-secondary"
          @click="showAll3DEvaluationValues"
        >
          <i class="bi bi-eye me-1"></i>Show all
        </button>
      </div>
      <div class="cube-3d-filters__chips">
        <button
          v-for="[id, option] in judgmentOptionEntries"
          :key="id"
          type="button"
          class="cube-3d-chip"
          :class="{ 'cube-3d-chip--active': visible3DEvaluationValues.has(id) }"
          :style="{
            '--chip-color': option.color,
            backgroundColor: visible3DEvaluationValues.has(id) ? option.color : 'transparent',
            borderColor: option.color,
            color: visible3DEvaluationValues.has(id) ? '#fff' : option.color
          }"
          @click="toggle3DEvaluationValue(id)"
        >
          {{ option.label }}
          <span class="cube-3d-chip__count">{{ cellCountsByEvaluationValue.get(id) || 0 }}</span>
        </button>
      </div>
    </div>

    <!-- Axis Manager Panel -->
    <div v-if="show3DAxisManager && editMode && canManageAxis" class="cube-3d-axis-manager">
      <div class="cube-3d-axis-manager__section">
        <h6 class="cube-3d-axis-manager__title">
          <span class="badge bg-danger me-1">X</span> Columns ({{ kdm?.columns?.length || 0 }})
        </h6>
        <div class="cube-3d-axis-manager__list">
          <div v-for="col in kdm?.columns" :key="col.id" class="cube-3d-axis-manager__item">
            <KdmEditableAxisLabel :axis="col" :can-edit="true" target="_blank" link-class="cube-3d-axis-manager__link"/>
            <button
              type="button"
              class="btn btn-sm btn-link text-danger p-0"
              @click="handleDeleteAxis('column', col.crossLinkId, col.id, col.title)"
              title="Delete"
            >
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>
        <div class="cube-3d-axis-manager__add">
          <input
            v-model="newColumnTitle"
            type="text"
            class="form-control form-control-sm"
            placeholder="New column..."
            @keydown.enter="handleAddColumn"
            :disabled="addingAxis"
          />
          <button
            class="btn btn-sm btn-outline-primary"
            @click="handleAddColumn"
            :disabled="addingAxis || !newColumnTitle.trim()"
          >
            <i class="bi bi-plus-lg"></i>
          </button>
          <button
            type="button"
            class="btn btn-sm btn-success"
            style="background-color: #198754; border-color: #198754; color: white; font-weight: bold;"
            @click="openAxisRecycle('column')"
            title="Recycle existing value"
          >
            <i class="bi bi-plus-lg"></i>
          </button>
        </div>
      </div>

      <div class="cube-3d-axis-manager__section">
        <h6 class="cube-3d-axis-manager__title">
          <span class="badge bg-success me-1">Y</span> Rows ({{ kdm?.rows?.length || 0 }})
        </h6>
        <div class="cube-3d-axis-manager__list">
          <div v-for="row in kdm?.rows" :key="row.id" class="cube-3d-axis-manager__item">
            <KdmEditableAxisLabel :axis="row" :can-edit="true" target="_blank" link-class="cube-3d-axis-manager__link"/>
            <button
              type="button"
              class="btn btn-sm btn-link text-danger p-0"
              @click="handleDeleteAxis('row', row.crossLinkId, row.id, row.title)"
              title="Delete"
            >
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>
        <div class="cube-3d-axis-manager__add">
          <input
            v-model="newRowTitle"
            type="text"
            class="form-control form-control-sm"
            placeholder="New row..."
            @keydown.enter="handleAddRow"
            :disabled="addingAxis"
          />
          <button
            class="btn btn-sm btn-outline-primary"
            @click="handleAddRow"
            :disabled="addingAxis || !newRowTitle.trim()"
          >
            <i class="bi bi-plus-lg"></i>
          </button>
          <button
            type="button"
            class="btn btn-sm btn-success"
            style="background-color: #198754; border-color: #198754; color: white; font-weight: bold;"
            @click="openAxisRecycle('row')"
            title="Recycle existing value"
          >
            <i class="bi bi-plus-lg"></i>
          </button>
        </div>
      </div>

      <div class="cube-3d-axis-manager__section">
        <h6 class="cube-3d-axis-manager__title">
          <span class="badge bg-primary me-1">Z</span> Stakeholders ({{ kdm?.stake_holders?.length || 0 }})
        </h6>
        <div class="cube-3d-axis-manager__list">
          <div v-for="sh in kdm?.stake_holders" :key="sh.id" class="cube-3d-axis-manager__item">
            <KdmEditableAxisLabel :axis="sh" :can-edit="true" target="_blank" link-class="cube-3d-axis-manager__link"/>
            <button
              type="button"
              class="btn btn-sm btn-link text-danger p-0"
              @click="handleDeleteAxis('stakeholder', sh.crossLinkId, sh.id, sh.title)"
              title="Delete"
            >
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>
        <div class="cube-3d-axis-manager__add">
          <input
            v-model="newStakeholderTitle"
            type="text"
            class="form-control form-control-sm"
            placeholder="New stakeholder..."
            @keydown.enter="handleAddStakeholder"
            :disabled="addingAxis"
          />
          <button
            class="btn btn-sm btn-outline-primary"
            @click="handleAddStakeholder"
            :disabled="addingAxis || !newStakeholderTitle.trim()"
          >
            <i class="bi bi-plus-lg"></i>
          </button>
          <button
            type="button"
            class="btn btn-sm btn-success"
            style="background-color: #198754; border-color: #198754; color: white; font-weight: bold;"
            @click="openAxisRecycle('stakeholder')"
            title="Recycle existing value"
          >
            <i class="bi bi-plus-lg"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="cube-3d-content" :class="{ 'cube-3d-content--with-drawer': show3DCellDetailMode && selected3DCellId }">
      <KDMCube3D
        :selected-axis="sliceAxis"
        :selected-index="activeSliceIndex"
        :show-axes="show3DAxes"
        :show-full-labels="true"
        :is-fullscreen="bsModalState"
        :visible-evaluation-values="visible3DEvaluationValues"
        :show-labels="show3DLabels"
        :show-all-opaque="show3DAllOpaque"
        :cell-detail-mode="show3DCellDetailMode"
        :selected-cell-id="selected3DCellId"
        @select-slice="onSelectSlice"
        @cell-click="on3DCellClick"
      />

      <!-- Cell Detail Drawer -->
      <div v-if="show3DCellDetailMode && selected3DCellId" class="cube-3d-drawer">
        <div class="cube-3d-drawer__header">
          <div class="cube-3d-drawer__title">
            <strong>{{ selected3DCellInfo?.colTitle }}</strong>
            <span class="text-muted mx-1">&times;</span>
            <strong>{{ selected3DCellInfo?.rowTitle }}</strong>
          </div>
          <button type="button" class="btn-close cube-3d-drawer__close" @click="close3DCellDrawer"></button>
        </div>

        <div class="cube-3d-drawer__body">
          <!-- Winning Judgment Summary with proportional bar -->
          <div v-if="selected3DCellData?.winningJudgment" class="cube-3d-drawer__summary">
            <div class="cube-3d-drawer__weight-bar-container">
              <div
                class="cube-3d-drawer__weight-bar"
                :style="{
                  backgroundColor: selected3DCellData.winningJudgment.color,
                  width: (selected3DCellData.winningJudgment.totalWeight ?? selected3DCellData.winningJudgment.sliderValue ?? 0) + '%'
                }"
              >
                <span class="cube-3d-drawer__weight-label">
                  {{ selected3DCellData.winningJudgment.label || 'Result' }}
                  ({{ selected3DCellData.winningJudgment.totalWeight ?? selected3DCellData.winningJudgment.sliderValue ?? 0 }}%)
                </span>
              </div>
            </div>
          </div>
          <div v-else class="cube-3d-drawer__empty text-muted">
            <i class="bi bi-inbox me-2"></i>
            No evaluation
          </div>

          <!-- Indicators List (KDM Type 2/3) -->
          <div v-if="selected3DCellData?.indicators?.length" class="cube-3d-drawer__indicators">
            <h6 class="cube-3d-drawer__section-title">
              <i class="bi bi-list-ul me-1"></i>
              Indicators ({{ selected3DCellData.indicators.length }})
            </h6>
            <div
              v-for="indicator in selected3DCellData.indicators"
              :key="indicator.id"
              class="cube-3d-drawer__indicator"
            >
              <div class="cube-3d-drawer__indicator-header">
                <a :href="indicator.href" target="_blank" class="cube-3d-drawer__indicator-title">
                  {{ indicator.title }}
                  <i class="bi bi-box-arrow-up-right ms-1 small"></i>
                </a>
              </div>
              <div class="cube-3d-drawer__indicator-meta">
                <span
                  class="cube-3d-drawer__indicator-judgment"
                  :style="{ backgroundColor: indicator.judgmentColor }"
                >
                  {{ indicator.judgmentLabel }}
                </span>
                <span class="cube-3d-drawer__indicator-weight">
                  {{ indicator.sliderValue }}%
                </span>
                <button
                  type="button"
                  class="btn btn-sm btn-link text-decoration-none p-0 border-0"
                  @click.stop="open3DIndicatorDiscussion(indicator.id)"
                  title="Discuss this indicator"
                >
                  <i class="bi" :class="[getIndicatorCommentIcon(indicator.id).icon, getIndicatorCommentIcon(indicator.id).class]" style="font-size: 0.85rem;"></i>
                </button>
                <button
                  v-if="editMode && !isSelected3DCellLocked"
                  type="button"
                  class="btn btn-sm btn-link text-danger p-0 border-0 ms-1"
                  @click.stop="remove3DSavedIndicator(indicator.id)"
                  title="Remove indicator"
                >
                  <i class="bi bi-trash" style="font-size: 0.85rem;"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Pending Indicators (newly added, need evaluation value and weight) -->
          <div v-if="selected3DCellData?.pendingIndicators?.length && editMode && !isSelected3DCellLocked" class="cube-3d-drawer__pending">
            <h6 class="cube-3d-drawer__section-title">
              <i class="bi bi-hourglass-split me-1 text-warning"></i>
              Pending ({{ selected3DCellData.pendingIndicators.length }})
            </h6>
            <div
              v-for="indicator in selected3DCellData.pendingIndicators"
              :key="indicator.id"
              class="cube-3d-drawer__pending-indicator"
            >
              <div class="cube-3d-drawer__pending-header">
                <span class="cube-3d-drawer__pending-title">{{ indicator.title }}</span>
                <button
                  type="button"
                  class="btn btn-sm btn-link text-danger p-0"
                  @click="remove3DPendingIndicator(indicator.id)"
                  title="Remove"
                >
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
              <div class="cube-3d-drawer__pending-form">
                <div class="cube-3d-drawer__pending-row">
                  <label class="cube-3d-drawer__pending-label">Value</label>
                  <select
                    class="form-select form-select-sm cube-3d-drawer__pending-select"
                    :value="getPending3DValue(indicator.id).judgmentId"
                    @change="(e) => updatePending3DIndicatorValue(indicator.id, { judgmentId: (e.target as HTMLSelectElement).value })"
                  >
                    <option value="">-- Select --</option>
                    <option
                      v-for="[id, option] in judgmentOptionEntries"
                      :key="id"
                      :value="id"
                    >
                      {{ option.title || option.color }}
                    </option>
                  </select>
                </div>
                <div class="cube-3d-drawer__pending-row">
                  <label class="cube-3d-drawer__pending-label">Weight</label>
                  <div class="cube-3d-drawer__pending-slider-group">
                    <input
                      type="range"
                      class="form-range cube-3d-drawer__pending-slider"
                      min="0"
                      max="100"
                      :value="getPending3DValue(indicator.id).sliderValue"
                      @input="(e) => updatePending3DIndicatorValue(indicator.id, { sliderValue: parseInt((e.target as HTMLInputElement).value) })"
                    >
                    <span class="cube-3d-drawer__pending-slider-value">
                      {{ getPending3DValue(indicator.id).sliderValue }}%
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-primary w-100 mt-2"
                  :disabled="!getPending3DValue(indicator.id).judgmentId"
                  @click="save3DPendingIndicator(indicator.id)"
                >
                  <i class="bi bi-check-lg me-1"></i>
                  Validate
                </button>
              </div>
            </div>
          </div>

          <!-- Add Indicator Button (Edit Mode only, KDM Type 2/3, not locked) -->
          <div v-if="kdm?.kdm_type !== 1 && editMode && !isSelected3DCellLocked" class="cube-3d-drawer__add-indicator">
            <button
              type="button"
              class="btn btn-sm btn-success w-100"
              @click="open3DIndicatorDrawer"
            >
              <i class="bi bi-plus-lg me-1"></i>
              Add indicator
            </button>
          </div>

          <!-- Locked indicator for assigned users -->
          <div v-if="kdm?.kdm_type !== 1 && editMode && isSelected3DCellLocked" class="cube-3d-drawer__locked text-center text-muted small py-2">
            <i class="bi bi-lock-fill me-1"></i>
            Locked by assignment
          </div>

          <!-- Cell Comments Section -->
          <div class="cube-3d-drawer__comments">
            <h6 class="cube-3d-drawer__section-title">
              <i class="bi" :class="[selected3DCellCommentIcon.icon, selected3DCellCommentIcon.class]"></i>
              <span class="ms-1">Cell discussion</span>
            </h6>
            <button
              type="button"
              class="btn btn-sm btn-outline-primary w-100"
              @click="open3DCellDiscussion"
            >
              <i class="bi" :class="[selected3DCellCommentIcon.icon]"></i>
              <span class="ms-1">Open discussion</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="cube-3d-legend">
      <span class="cube-3d-legend__label">{{ t('epb.kdm.click_layer') }}</span>
      <div class="cube-3d-legend__dots">
        <div
          v-for="(item, index) in currentSliceItems"
          :key="item.id"
          class="cube-3d-dot"
          :class="{ 'cube-3d-dot--active': index === activeSliceIndex }"
          :title="item.title"
          @click="onSelectSlice(sliceAxis, index)"
        ></div>
      </div>
    </div>
  </div>

  <!-- 2D View with Carousel navigation -->
  <div v-if="!show3DView && !showKnotView && kdm?.kdm_type && kdm?.stake_holders && kdm.stake_holders.length > 0" :style="{ '--kdm-column-width': columnWidth + 'px' }">
    <!-- The "Voir par : SH / TOC / QPI" switch is rendered inside the table's
         top-left corner cell (KDMViewAxisToggle) to reclaim that empty space. -->

    <!-- Stakeholder view (unchanged) -->
    <template v-if="viewAxis === 'stakeholder'">
      <MatrixAxisCarousel
        :items="carouselItems"
        :model-value="activeStakeholderId || kdm.stake_holders[0].id"
        @update:model-value="(val: string) => activeStakeholderId = val"
      />
      <!-- Full CUD for the stakeholder (Z) axis, directly from the matrix view. -->
      <div v-if="editMode && canManageAxis" class="d-flex flex-wrap justify-content-center align-items-center gap-3 mb-2">
        <!-- Update (rename) / Delete the active stakeholder -->
        <div v-if="activeStakeholder" class="d-inline-flex align-items-center gap-2">
          <span class="text-muted small">Stakeholder:</span>
          <KdmEditableAxisLabel :axis="activeStakeholder" :can-edit="true" icon-stroke="#212529"/>
          <button
            class="btn btn-sm btn-outline-danger"
            @click="handleDeleteAxis('stakeholder', activeStakeholder.crossLinkId, activeStakeholder.id, activeStakeholder.title)"
            title="Delete this stakeholder"
          >
            <i class="bi bi-trash"></i>
          </button>
        </div>
        <!-- Create a new stakeholder -->
        <div class="input-group input-group-sm" style="max-width: 280px;">
          <input
            v-model="newStakeholderTitle"
            type="text"
            class="form-control"
            placeholder="New stakeholder..."
            @keydown.enter="handleAddStakeholder"
            :disabled="addingAxis"
          />
          <button
            class="btn btn-outline-primary"
            @click="handleAddStakeholder"
            :disabled="addingAxis || !newStakeholderTitle.trim()"
            title="Add stakeholder"
          >
            <i class="bi bi-plus-lg"></i>
          </button>
          <button
            type="button"
            class="btn btn-success"
            style="background-color: #198754; border-color: #198754; color: white; font-weight: bold;"
            @click="openAxisRecycle('stakeholder')"
            title="Recycle existing value"
          >
            <i class="bi bi-plus-lg"></i>
          </button>
        </div>
      </div>
      <!-- Matrix content for active stakeholder -->
      <Transition name="matrix-fade" mode="out-in">
        <div class="dm__table-wrapper" :key="activeStakeholderId || kdm.stake_holders[0].id">
          <template v-for="tab in kdm.stake_holders" :key="tab.id">
            <KDMTable v-if="(activeStakeholderId || kdm.stake_holders[0].id) === tab.id" :tab="tab"/>
          </template>
        </div>
      </Transition>
      <MatrixLegend :items="legendItems" :kik-note-text="kikNoteText" :hide-kik-note="!hasMissingIpr" />
    </template>

    <!-- Pivoted view: TOC or QPI as the page, Stakeholders as columns -->
    <template v-else>
      <MatrixAxisCarousel
        v-if="pivotCarouselItems.length"
        :items="pivotCarouselItems"
        :model-value="activePivotTab?.id || ''"
        @update:model-value="(val: string) => activePivotId = val"
      />
      <Transition name="matrix-fade" mode="out-in">
        <div class="dm__table-wrapper" :key="viewAxis + ':' + (activePivotTab?.id || '')">
          <KDMPivotTable v-if="activePivotTab" :view-axis="(viewAxis as 'toc' | 'qpi')" :tab="activePivotTab"/>
        </div>
      </Transition>
      <MatrixLegend :items="legendItems" :kik-note-text="kikNoteText" :hide-kik-note="!hasMissingIpr" />
    </template>
  </div>
  <div class="modal fade" id="evaluationValuesModal" tabindex="-1" aria-labelledby="evaluationValuesModalTitle"
       aria-modal="true" role="dialog">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h1 class="modal-title fs-5" id="evaluationValuesModalTitle">{{ t('epb.kdm.judgment_values') }}</h1>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="dm__judgment">
            <div class="mb-4">
              <button
                type="button"
                class="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                :disabled="addingJudgmentOption"
                @click="addJudgmentOption"
              >
                <SpinnerIcon v-if="addingJudgmentOption" :size="16" />
                <template v-else>
                  <i class="bi bi-plus-lg"></i>
                  <span>{{ t('epb.kdm.judgment.create') }}</span>
                </template>
              </button>
            </div>
            <div class="dm__judgment-list" v-if="judgmentOptionEntries.length">
              <KDMJudgmentOption
                v-for="([key], i) in judgmentOptionEntries"
                :key="key"
                :judgment-id="key"
                :index="i"
              />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
  <KDMAssignmentsModal ref="assignmentsModal" />

  <!-- Axis Deletion Modal -->
  <Teleport to="body">
    <div v-if="axisDeleteModal.visible" class="axis-delete-overlay" @click.self="closeAxisDeleteModal">
      <div class="axis-delete-modal">
        <div class="axis-delete-modal__header">
          <h5 class="axis-delete-modal__title">
            <i class="bi bi-trash me-2"></i>
            {{ t('epb.kdm.delete_axis_title') }}: {{ axisDeleteModal.title }}
          </h5>
          <button type="button" class="btn-close" @click="closeAxisDeleteModal" :disabled="axisDeleteModal.loading"></button>
        </div>

        <div class="axis-delete-modal__body">
          <!-- Loading state -->
          <div v-if="axisDeleteModal.loading && !axisDeleteModal.usage" class="text-center py-4">
            <SpinnerIcon :size="32" />
            <p class="mt-2 text-muted">{{ t('epb.kdm.checking_usage') }}</p>
          </div>

          <!-- Usage info loaded -->
          <template v-else-if="axisDeleteModal.usage">
            <!-- Warning about other matrices -->
            <div v-if="axisDeleteModal.usage.otherMatrices.length > 0" class="alert alert-warning">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>{{ t('epb.kdm.axis_used_elsewhere') }}</strong>
              <ul class="mb-0 mt-2">
                <li v-for="matrix in axisDeleteModal.usage.otherMatrices" :key="matrix.id">
                  <a :href="`/node/${matrix.id}`" target="_blank">{{ matrix.title }}</a>
                  <span class="badge bg-secondary ms-1">{{ matrix.type }}</span>
                </li>
              </ul>
            </div>

            <!-- Info about dependent indicators -->
            <div v-if="axisDeleteModal.usage.dependentIndicators.length > 0" class="alert alert-info">
              <i class="bi bi-info-circle-fill me-2"></i>
              <strong>{{ t('epb.kdm.dependent_indicators', { count: axisDeleteModal.usage.dependentIndicators.length }) }}</strong>
              <ul class="mb-0 mt-2 axis-delete-modal__indicator-list">
                <li v-for="ind in axisDeleteModal.usage.dependentIndicators.slice(0, 5)" :key="ind.id">
                  {{ ind.title }}
                </li>
                <li v-if="axisDeleteModal.usage.dependentIndicators.length > 5" class="text-muted">
                  ... {{ t('epb.kdm.and_more', { count: axisDeleteModal.usage.dependentIndicators.length - 5 }) }}
                </li>
              </ul>
            </div>

            <!-- KRR cascade warning for KDM type 3 -->
            <div v-if="axisDeleteModal.usage.linkedKrrId && kdm?.kdm_type === 3" class="alert alert-danger">
              <i class="bi bi-exclamation-octagon-fill me-2"></i>
              <strong>{{ t('epb.kdm.cascade_krr_warning') }}</strong>
              <p class="mb-0 mt-1 small">{{ t('epb.kdm.cascade_krr_explanation') }}</p>
            </div>

            <!-- No issues found -->
            <div v-if="axisDeleteModal.usage.otherMatrices.length === 0 && axisDeleteModal.usage.dependentIndicators.length === 0" class="alert alert-success">
              <i class="bi bi-check-circle-fill me-2"></i>
              {{ t('epb.kdm.axis_safe_to_delete') }}
            </div>

            <!-- Delete node toggle -->
            <div class="axis-delete-modal__option">
              <div class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="deleteNodeToggle"
                  v-model="axisDeleteModal.deleteNode"
                >
                <label class="form-check-label" for="deleteNodeToggle">
                  <strong>{{ t('epb.kdm.delete_node_completely') }}</strong>
                </label>
              </div>
              <p class="small text-muted mt-1 mb-0">
                <template v-if="axisDeleteModal.deleteNode">
                  <i class="bi bi-exclamation-triangle text-warning me-1"></i>
                  {{ t('epb.kdm.delete_node_warning') }}
                </template>
                <template v-else>
                  <i class="bi bi-info-circle me-1"></i>
                  {{ t('epb.kdm.delete_link_only') }}
                </template>
              </p>
            </div>
          </template>
        </div>

        <div class="axis-delete-modal__footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="closeAxisDeleteModal"
            :disabled="axisDeleteModal.loading"
          >
            {{ t('epb.cancel') }}
          </button>
          <button
            type="button"
            class="btn"
            :class="axisDeleteModal.deleteNode ? 'btn-danger' : 'btn-warning'"
            @click="confirmAxisDelete"
            :disabled="axisDeleteModal.loading || !axisDeleteModal.usage"
          >
            <SpinnerIcon v-if="axisDeleteModal.loading" :size="16" class="me-1" />
            <i v-else class="bi bi-trash me-1"></i>
            {{ axisDeleteModal.deleteNode ? t('epb.kdm.delete_permanently') : t('epb.kdm.remove_from_matrix') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Recycle an existing briquette as an axis value -->
  <AxisRecycleDrawer
    v-model:visible="axisRecycle.visible"
    :nid="kdm?.nid"
    :axis-type="axisRecycle.axisType"
    @recycled="onAxisRecycled"
  />
</template>

<style scoped>
.dm__table-wrapper {
  max-width: 100%;
  overflow-x: auto;
  overflow-y: auto;
  max-height: 90vh;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  background: white;
  border: 1px solid #e0e0e0;
}

.matrix-fade-enter-active,
.matrix-fade-leave-active {
  transition: all 0.35s ease-out;
}

.matrix-fade-enter-from {
  opacity: 0;
  transform: translateX(60px) rotateY(-5deg);
}

.matrix-fade-leave-to {
  opacity: 0;
  transform: translateX(-60px) rotateY(5deg);
}

.dm__judgment-list {
  margin-top: 1rem;
}

.lock-status-indicator {
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  background-color: rgba(0, 0, 0, 0.05);
}

/* 3D Cube View */
.cube-3d-container {
  margin-top: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 12px;
  overflow: hidden;
}

.cube-3d-container--fullscreen {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 0;
}

.cube-3d-container--fullscreen .cube-3d-content {
  flex: 1 1 auto;
  min-height: 0;
}

.cube-3d-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  gap: 1rem;
}

.cube-3d-header__left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cube-3d-header__left > i {
  font-size: 1.1rem;
}

.cube-3d-header__left .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
}

.cube-3d-axis-selector {
  display: flex;
  gap: 2px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 2px;
}

.cube-3d-axis-selector .btn {
  min-width: 28px;
  font-weight: 600;
}

.cube-3d-separator {
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 0.25rem;
}

.cube-3d-axis-label {
  font-weight: 600;
  margin-right: 0.25rem;
}

.cube-3d-info {
  font-size: 0.85rem;
  opacity: 0.9;
  background: rgba(255, 255, 255, 0.2);
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  min-width: 200px;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cube-3d-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
}

.cube-3d-legend__label {
  font-size: 0.8rem;
  color: #6c757d;
}

.cube-3d-legend__dots {
  display: flex;
  gap: 0.5rem;
}

.cube-3d-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #dee2e6;
  cursor: pointer;
  transition: all 0.2s;
}

.cube-3d-dot:hover {
  transform: scale(1.3);
  background: #adb5bd;
}

.cube-3d-dot--active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  transform: scale(1.2);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
}

/* 3D Filter badge */
.filter-badge-3d {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 8px;
  height: 8px;
  background-color: var(--bs-danger);
  border-radius: 50%;
}

/* 3D Filters Bar */
.cube-3d-filters {
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.cube-3d-filters__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.cube-3d-filters__title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
}

.cube-3d-filters__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.cube-3d-chip {
  padding: 0.25rem 0.75rem;
  border: 2px solid;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cube-3d-chip:hover {
  transform: scale(1.05);
}

.cube-3d-chip--active {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.cube-3d-chip__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 5px;
  margin-left: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.3);
}

.cube-3d-chip--active .cube-3d-chip__count {
  background: rgba(0, 0, 0, 0.2);
}

/* Axis Manager Panel */
.cube-3d-axis-manager {
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.cube-3d-axis-manager__section {
  flex: 1;
  min-width: 200px;
}

.cube-3d-axis-manager__title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
}

.cube-3d-axis-manager__list {
  max-height: 150px;
  overflow-y: auto;
  margin-bottom: 0.5rem;
}

.cube-3d-axis-manager__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.25rem 0.5rem;
  background: #fff;
  border-radius: 4px;
  margin-bottom: 0.25rem;
  font-size: 0.85rem;
}

.cube-3d-axis-manager__link {
  text-decoration: none;
  color: #495057;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cube-3d-axis-manager__link:hover {
  color: #0d6efd;
  text-decoration: underline;
}

.cube-3d-axis-manager__add {
  display: flex;
  gap: 0.25rem;
}

.cube-3d-axis-manager__add input {
  flex: 1;
}

/* 3D Content with drawer layout */
.cube-3d-content {
  position: relative;
  display: flex;
  min-height: 500px;
}

.cube-3d-content .kdm-cube-3d {
  flex: 1;
  min-width: 0;
  min-height: 500px;
}

/* 3D Cell Detail Drawer */
.cube-3d-drawer {
  width: 320px;
  min-width: 320px;
  background: #fff;
  border-left: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
  max-height: 500px;
  overflow: hidden;
}

.cube-3d-drawer__header {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
  position: relative;
}

.cube-3d-drawer__title {
  font-size: 0.9rem;
  padding-right: 24px;
}

.cube-3d-drawer__subtitle {
  font-size: 0.75rem;
  margin-top: 2px;
}

.cube-3d-drawer__close {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 0.75rem;
}

.cube-3d-drawer__body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
}

.cube-3d-drawer__summary {
  margin-bottom: 1rem;
}

.cube-3d-drawer__weight-bar-container {
  width: 100%;
  height: 32px;
  background: #e9ecef;
  border-radius: 6px;
  overflow: hidden;
}

.cube-3d-drawer__weight-bar {
  height: 100%;
  min-width: fit-content;
  display: flex;
  align-items: center;
  border-radius: 6px;
  transition: width 0.3s ease;
}

.cube-3d-drawer__weight-label {
  padding: 0 0.75rem;
  color: #fff;
  font-weight: 600;
  font-size: 0.85rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
}

.cube-3d-drawer__empty {
  padding: 1rem;
  text-align: center;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.cube-3d-drawer__section-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.cube-3d-drawer__indicators {
  margin-bottom: 1rem;
}

.cube-3d-drawer__indicator {
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 0.5rem;
}

.cube-3d-drawer__indicator:last-child {
  margin-bottom: 0;
}

.cube-3d-drawer__indicator-header {
  margin-bottom: 0.25rem;
}

.cube-3d-drawer__indicator-title {
  font-size: 0.85rem;
  color: #0d6efd;
  text-decoration: none;
}

.cube-3d-drawer__indicator-title:hover {
  text-decoration: underline;
}

.cube-3d-drawer__indicator-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cube-3d-drawer__indicator-judgment {
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 4px;
  color: #fff;
  font-weight: 500;
}

.cube-3d-drawer__indicator-weight {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
}

.cube-3d-drawer__add-indicator {
  margin-bottom: 1rem;
}

/* Pending indicators section */
.cube-3d-drawer__pending {
  margin-bottom: 1rem;
}

.cube-3d-drawer__pending-indicator {
  padding: 0.75rem;
  background: linear-gradient(135deg, #fff8e1 0%, #fff3c4 100%);
  border: 1px solid #ffc107;
  border-radius: 8px;
  margin-bottom: 0.5rem;
}

.cube-3d-drawer__pending-indicator:last-child {
  margin-bottom: 0;
}

.cube-3d-drawer__pending-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.cube-3d-drawer__pending-title {
  font-size: 0.85rem;
  font-weight: 500;
  color: #333;
}

.cube-3d-drawer__pending-form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cube-3d-drawer__pending-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cube-3d-drawer__pending-label {
  font-size: 0.75rem;
  color: #666;
  min-width: 45px;
}

.cube-3d-drawer__pending-select {
  flex: 1;
  font-size: 0.8rem;
}

.cube-3d-drawer__pending-slider-group {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cube-3d-drawer__pending-slider {
  flex: 1;
}

.cube-3d-drawer__pending-slider-value {
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  min-width: 35px;
  text-align: right;
}

.cube-3d-drawer__comments {
  padding-top: 0.5rem;
  border-top: 1px solid #dee2e6;
}

.animate-bounce {
  display: inline-block;
  animation: bounce 1s ease infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

/* Axis Delete Modal */
.axis-delete-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
}

/* Full-screen scrollable overlay hosting the multi-page Deliberation Report.
   On print, it expands to the natural document flow so all pages are emitted. */
/* The Matrix report reuses the SHARED 90% drawer chrome (.epb-pt-drawer) for
   coherence with the Manage / Today / Collection drawers; only the scroll +
   background and a dedicated backdrop are kept here. */
.kdm-report-overlay {
  overflow: auto;
  background: #f1f3f5;
}

.kdm-report-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1049;
}

@media print {
  /* Print full-width: neutralise the drawer chrome so the report fills the page. */
  .kdm-report-overlay.epb-pt-drawer {
    position: static;
    width: auto;
    max-width: none;
    transform: none;
    inset: auto;
    overflow: visible;
    box-shadow: none;
  }
  .kdm-report-backdrop {
    display: none !important;
  }
}

.axis-delete-modal {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.axis-delete-modal__header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
}

.axis-delete-modal__title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.axis-delete-modal__header .btn-close {
  filter: brightness(0) invert(1);
  opacity: 0.8;
}

.axis-delete-modal__header .btn-close:hover {
  opacity: 1;
}

.axis-delete-modal__body {
  padding: 1.25rem;
  overflow-y: auto;
  flex: 1;
}

.axis-delete-modal__indicator-list {
  font-size: 0.85rem;
  max-height: 120px;
  overflow-y: auto;
}

.axis-delete-modal__option {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 1rem;
}

.axis-delete-modal__footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  background: #f8f9fa;
}
</style>

<!-- Non-scoped styles for 3D header (scoped CSS has issues in production) -->
<style>
.cube-3d-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  gap: 1rem;
}

.cube-3d-header__left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cube-3d-header__left > i {
  font-size: 1.1rem;
}

.cube-3d-header__left .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
}

.cube-3d-axis-selector {
  display: flex;
  gap: 2px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 2px;
}

.cube-3d-axis-selector .btn {
  min-width: 28px;
  font-weight: 600;
}

.cube-3d-separator {
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 0.25rem;
}

.cube-3d-info {
  font-size: 0.85rem;
  opacity: 0.9;
  background: rgba(255, 255, 255, 0.2);
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  min-width: 200px;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cube-3d-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
}

.cube-3d-legend__label {
  font-size: 0.8rem;
  color: #6c757d;
}

/* When the Deliberation Report overlay is open, print ONLY the report. The
   overlay is teleported to <body>, so we hide every other direct body child. */
@media print {
  body.kdm-report-open > *:not(.kdm-report-overlay) {
    display: none !important;
  }
}
</style>
